import * as React from "react";
import { useState, useRef, useEffect } from "react";
import * as Immutable from "immutable";
import { Alert } from "components/bootstrap";
import BootstrapModalConfirm from "components/bootstrap/BootstrapModalConfirm";
import { Spinner, PaginatedList, Icon } from "components/common";
import MessageTable from "components/common/message/messagetable/MessageTable";
import MessagesWidgetConfig from "views/logic/widgets/MessagesWidgetConfig";
import { DEFAULT_MESSAGE_FIELDS, TIMESTAMP_FIELD } from "views/Constants";
import useFieldTypes from "views/logic/fieldtypes/useFieldTypes";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import WindowDimensionsContextProvider from "contexts/WindowDimensionsContextProvider";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import AutoRefreshDisabledProvider from "report/common/AutoRefreshDisabledProvider";
import FieldActionsContext from "views/components/actions/FieldActionsContext";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import { useRemoveEvidence } from "security-app/hooks/useInvestigationsAPI";
import {
  CopyToClipboardFieldActionPlugin,
  AddFavoriteFieldActionPlugin,
  RemoveFavoriteFieldActionPlugin
} from "views/actions/FieldActionPlugins";
import { CopyToClipBoardValueActionPlugin, CreateExtractorValueActionPlugin } from "views/actions/ValueActionPlugins";
import { InsertIntoViewValueActionPlugin } from "enterprise/parameters/actions/ValueActionPlugins";
import usePaginatedInvestigationLogs from "./usePaginatedInvestigationLogs";
import { IconButton } from "../styled.components";
const PAGE_SIZE = 10;
const toBackendMessage = (log, listIndex, currentPage) => {
  var _a, _b, _c;
  const fallbackId = `investigation-log-${currentPage}-${listIndex}`;
  const messageId = String((_b = (_a = log.message) == null ? void 0 : _a._id) != null ? _b : fallbackId);
  const timestamp = typeof ((_c = log.message) == null ? void 0 : _c.timestamp) === "string" ? log.message.timestamp : "";
  return {
    index: log.index,
    message: {
      ...log.message,
      _id: messageId,
      timestamp
    },
    highlight_ranges: log.highlight_ranges,
    decoration_stats: log.decoration_stats
  };
};
function LogsList({ investigation }) {
  var _a;
  const { permissions } = useCurrentUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLog, setDeleteLog] = useState(null);
  const scrollContainerRef = useRef();
  const { removeEvidence, removingEvidence } = useRemoveEvidence();
  const { data: fieldTypes, isLoading: loadingFieldTypes } = useFieldTypes([], void 0);
  const allFieldTypes = Immutable.List(fieldTypes != null ? fieldTypes : []);
  const fieldTypesContextValue = {
    all: allFieldTypes,
    currentQuery: allFieldTypes
  };
  const actionConfig = {
    evaluateCondition: (conditionFn, handlerArgs) => conditionFn(handlerArgs),
    additionalHandlerArgs: {},
    fieldActions: [CopyToClipboardFieldActionPlugin, AddFavoriteFieldActionPlugin, RemoveFavoriteFieldActionPlugin],
    valueActions: [InsertIntoViewValueActionPlugin, CreateExtractorValueActionPlugin, CopyToClipBoardValueActionPlugin]
  };
  const setLoadingState = () => void 0;
  const canManageEvidence = !investigation.archived && isPermitted(permissions, "investigations:edit");
  useEffect(() => {
    setCurrentPage(1);
  }, [investigation.id, investigation.updated_at]);
  const { logs, totalLogs, loadingLogs, isLogsError, fetchError, hasLoadedLogs } = usePaginatedInvestigationLogs({
    investigationId: investigation.id,
    investigationUpdatedAt: investigation.updated_at,
    currentPage,
    pageSize: PAGE_SIZE,
    fallbackTotal: (_a = investigation.logs_count) != null ? _a : 0
  });
  const messages = logs.map(
    (log, listIndex) => toBackendMessage(log, listIndex, currentPage)
  );
  const onChangePage = (newCurrentPage) => {
    setCurrentPage(newCurrentPage);
  };
  const onDelete = (id, index) => (e) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
    setDeleteLog({ id, index });
  };
  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteLog(null);
  };
  const removeLog = async () => {
    if (!deleteLog) {
      return;
    }
    await removeEvidence({
      invId: investigation.id,
      payload: { logs: [{ id: deleteLog.id, index: deleteLog.index }] }
    });
    onCancelDelete();
  };
  const renderLogActions = (message) => /* @__PURE__ */ React.createElement(IconButton, { $danger: true, "aria-label": `Delete log ${message.id}`, onClick: onDelete(message.id, message.index) }, /* @__PURE__ */ React.createElement(Icon, { name: "close" }));
  const rowOverride = (message) => {
    var _a2;
    return Object.keys((_a2 = message.fields) != null ? _a2 : {}).every((fieldName) => fieldName === "_id" || fieldName === TIMESTAMP_FIELD) ? `This log message with ID ${message.id} is no longer available. It could not be found in its source index.` : void 0;
  };
  if (loadingLogs && !hasLoadedLogs) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading logs..." });
  }
  if (loadingFieldTypes) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading fields..." });
  }
  if (isLogsError && !hasLoadedLogs) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, fetchError);
  }
  if (totalLogs === 0) {
    return /* @__PURE__ */ React.createElement("div", { style: { color: "gray" } }, "There are no logs to list");
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, isLogsError && hasLoadedLogs && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, fetchError), /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      onChange: onChangePage,
      activePage: currentPage,
      useQueryParameter: false,
      pageSize: PAGE_SIZE,
      showPageSizeSelect: false,
      totalItems: totalLogs
    },
    /* @__PURE__ */ React.createElement(AutoRefreshDisabledProvider, null, /* @__PURE__ */ React.createElement(WindowDimensionsContextProvider, null, /* @__PURE__ */ React.createElement(FieldTypesContext.Provider, { value: fieldTypesContextValue }, /* @__PURE__ */ React.createElement(InteractiveContext.Provider, { value: true }, /* @__PURE__ */ React.createElement(FieldActionsContext.Provider, { value: actionConfig }, /* @__PURE__ */ React.createElement(
      MessageTable,
      {
        config: MessagesWidgetConfig.builder().fields(DEFAULT_MESSAGE_FIELDS).showMessageRow(true).showSummary(true).decorators([]).sort([]).build(),
        scrollContainerRef,
        fields: allFieldTypes,
        setLoadingState,
        messages,
        renderRowActions: canManageEvidence ? renderLogActions : void 0,
        rowOverride
      }
    ))))))
  ), /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: showDeleteDialog,
      title: "Delete Log",
      onConfirm: removeLog,
      onCancel: onCancelDelete,
      confirmButtonDisabled: removingEvidence
    },
    "Are you sure you want to delete this Log?"
  ));
}
export default LogsList;
